import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SlideInterfaceModel } from '../../../../../models/slide.model';
import { SponsorCardModel } from '../../../../../models/sponsor-card.model';
import {
  TrainingCategoryCreateUpdateModel,
  TrainingCategoryModel,
} from '../../../../../models/training-category.model';
import { AlertService } from '../../../../../services/alert.service';
import { SponsorCardService } from '../../../../../services/sponsor-card.service';
import { TrainingCategoryService } from '../../../../../services/training-category.service';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
} from '../../../../../utils/form.utils';

@Component({
  selector: 'app-training-categories-formular',
  templateUrl: './training-categories-formular.component.html',
  styleUrl: './training-categories-formular.component.scss',
  standalone: false,
})
export class TrainingCategoriesFormularComponent implements OnInit {
  private trainingCategory: TrainingCategoryModel;
  // Constructor
  constructor(
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private courseTypeService: TrainingCategoryService,
    private sponsorCardService: SponsorCardService,
    private alertService: AlertService
  ) {
    this.createForm();
  }

  // Variables
  public isLoading: boolean = true;
  public editMode: boolean = false;

  public trainingCategoryForm: FormGroup;
  public sponsorCardOptions: SponsorCardModel[];

  public selectedSponsorCards: SponsorCardModel[] = [];

  public fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public getMaxLengthFromValidator = getMaxLengthFromValidator;

  public async ngOnInit() {
    const id = this.activatedRoute.snapshot.params['trainingCategoryId'];
    if (id) {
      this.editMode = true;
      await this.getTrainingCategory(+atob(id));
    }
    this.getSponsorCards();
    this.updateMenuItems();
    this.isLoading = false;
  }

  public categoryMenuItems = [];

  private updateMenuItems(): void {
    this.categoryMenuItems = [
      {
        label: 'Löschen',
        icon: 'fa-regular fa-trash-can',
        command: () => {
          this.deleteTrainingCategory(this.trainingCategory.id);
        },
      },
    ];
  }

  private async getSponsorCards(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.sponsorCardService.getSponsorCards().subscribe({
        next: sponsorCards => {
          this.sponsorCardOptions = sponsorCards;
          resolve();
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Die Sponsoren konnten nicht geladen werden.'
          );
          reject();
        },
      });
    });
  }

  private async getTrainingCategory(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.courseTypeService.getTrainingCategoryById(id).subscribe({
        next: type => {
          this.trainingCategory = type;
          this.trainingCategoryForm.patchValue({
            name: type.name,
            color50: type.color50,
            color0: type.color0,
            color70: type.color70,
            color80: type.color80,
            color90: type.color90,
            descriptionBold: type.descriptionBold || '',
            description: type.description || '',
            picture: type.titlePicture,
            sponsorCards: type.sponsorCardIds,
          });
          resolve();
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Die Kategorie konnte nicht geladen werden.'
          );
          reject();
        },
      });
    });
  }

  private createForm() {
    this.trainingCategoryForm = new FormGroup({
      name: new FormControl('', Validators.required),
      color50: new FormControl('', Validators.required),
      color0: new FormControl('', Validators.required),
      color70: new FormControl('', Validators.required),
      color80: new FormControl('', Validators.required),
      color90: new FormControl('', Validators.required),
      descriptionBold: new FormControl(''),
      description: new FormControl('', [
        Validators.required,
        Validators.maxLength(500),
      ]),
      picture: new FormControl(''),
      sponsorCards: new FormControl(null),
    });
  }

  private createTrainingCategory(
    trainingCategory: TrainingCategoryCreateUpdateModel
  ) {
    this.courseTypeService.createTrainingCategory(trainingCategory).subscribe({
      next: () => {
        this.alertService.showSuccessAlert(
          'Die Kategorie wurde erfolgreich erstellt.'
        );
        this.location.back();
      },
      error: () => {
        this.alertService.showErrorAlert(
          'Die Kategorie konnte nicht erstellt werden.'
        );
      },
    });
  }

  private updateTrainingCategory(
    trainingCategory: TrainingCategoryCreateUpdateModel
  ) {
    this.courseTypeService.updateTrainingCategory(trainingCategory).subscribe({
      next: () => {
        this.alertService.showSuccessAlert(
          'Die Kategorie wurde erfolgreich aktualisiert.'
        );
        this.location.back();
      },
      error: () => {
        this.alertService.showErrorAlert(
          'Die Kategorie konnte nicht aktualisiert werden.'
        );
      },
    });
  }

  private deleteTrainingCategory(id: number) {
    this.courseTypeService.deleteCourseType(id).subscribe({
      next: () => {
        this.alertService.showSuccessAlert(
          'Die Kategorie wurde erfolgreich gelöscht.'
        );
        this.location.back();
      },
      error: () => {
        this.alertService.showErrorAlert(
          'Die Kategorie konnte nicht gelöscht werden.'
        );
      },
    });
  }

  public emptyCard: SlideInterfaceModel = {
    title: 'Bild hinzufügen',
  };

  public onColorSelected(color: string): void {
    const colors = this.trainingCategoryForm.get('colors') as FormArray;
    colors.push(new FormControl(color));
  }

  public onCancel(): void {
    this.location.back();
  }

  public onPreview(): void {
    console.debug('Preview');
  }

  public onPublish(): void {
    if (this.trainingCategoryForm.invalid) {
      return;
    }

    const trainingCategory: TrainingCategoryCreateUpdateModel = {
      id: this.trainingCategory?.id ?? null,
      name: this.trainingCategoryForm.get('name').value,
      color50: this.trainingCategoryForm.get('color50').value,
      color0: this.trainingCategoryForm.get('color0').value,
      color70: this.trainingCategoryForm.get('color70').value,
      color80: this.trainingCategoryForm.get('color80').value,
      color90: this.trainingCategoryForm.get('color90').value,
      descriptionBold: this.trainingCategoryForm.get('descriptionBold').value,
      description: this.trainingCategoryForm.get('description').value,
      title_picture: this.trainingCategoryForm.get('picture').value,
      sponsorCardIds: this.trainingCategoryForm.get('sponsorCards').value,
    };

    this.editMode
      ? this.updateTrainingCategory(trainingCategory)
      : this.createTrainingCategory(trainingCategory);
  }
}
